import { EventChannel } from '@redux-saga/core';
import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, cancel, cancelled, fork, put, race, select, take, takeLeading } from 'typed-redux-saga';

import {
    isRoomAgentMode,
    selectIsMeetingRoomIntegration,
    selectIsTranslatorIntegration,
} from '../../../../shared/features/config/selectors';
import { broadcastManager } from '../../../../shared/features/goloom-sdk';
import { NOTIFICATIONS_MAP, notify } from '../../../../shared/features/notifications/ps-notifications';
import { isMobileUa } from '../../../../shared/features/uatraits/selectors';
import { copy } from '../../../../shared/helpers/clipboard';
import { toLoggableError } from '../../../../shared/helpers/errors';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import { CloudAPI } from '../../../../shared/helpers/transport/cloud-api/types';
import { createVHLiveEventsChannel, VHLiveEventData } from '../../../../shared/helpers/vh-live-events';
import { BroadcastData, BroadcastStatus } from '../../../../types';
import {
    getBroadcastData,
    getBroadcastStatus,
    getBroadcastUri,
    getUgcLiveUrl,
    getVhliveUgcStatus,
    isChatAllowedInConference,
    selectIsBroadcastEnabled,
    selectVideoGrid,
    selectYaServices,
} from '../../selectors';
import { CreateBroadcastActionPayload, NotificationId, SidebarContent } from '../../types';
import { getJoinedChatId, joinTheChatSaga, setJoinedChatId } from '../chat';
import {
    cancelCallAction,
    initBroadcastSettings,
    setRoomConferenceConfig,
    updateBroadcastSettings,
} from '../conference/slice';
import { setSidebarContent } from '../sidebar/slice';
import {
    createBroadcastAction,
    endBroadcastAction,
    resetVHLiveState,
    shareBroadcastAction,
    startBroadcastAction,
    stopBroadcastAction,
    updateVHLiveState,
} from './slice';

const logger = createLogger('goloom:broadcast');

function* createBroadcastSaga({
    payload: { autoStart, caption, description, accessLevel } = {},
}: PayloadAction<CreateBroadcastActionPayload>) {
    if (!broadcastManager.isReady) {
        return;
    }

    try {
        const yaServices = yield* select(selectYaServices);
        const isBroadcastEnabled = yield* select(selectIsBroadcastEnabled);
        if (!isBroadcastEnabled) {
            const { yandex360 } = yaServices;
            window.open(`${yandex360}/business/tariff/`);
            return;
        }

        const payload: CloudAPI.CreateBroadcast.Request = { caption, description };

        if (accessLevel) {
            payload.access_level = accessLevel;
        }

        const createBroadcastData = yield* call([broadcastManager, broadcastManager.createBroadcast], payload);
        if (!createBroadcastData) {
            throw new Error('No createBroadcastData');
        }

        yield* put(initBroadcastSettings(createBroadcastData));

        if (autoStart) {
            yield* put(startBroadcastAction());
        }
    } catch (error) {
        logger.error(
            toLoggableError(error, {
                method: 'createBroadcastSaga',
            })
        );

        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.CREATE_BROADCAST_FAILED]()));
    }
}

function* startBroadcastSaga() {
    if (!broadcastManager.isReady) {
        return;
    }

    const previousStatus = yield* select(getBroadcastStatus);

    try {
        yield* put(resetVHLiveState());
        yield* put(updateBroadcastSettings({ status: BroadcastStatus.STARTING }));
        const videoGrid = yield* select(selectVideoGrid);
        const startBroadcastData = yield* call(
            [broadcastManager, broadcastManager.startBroadcast],
            videoGrid.activeGridView
        );
        if (!startBroadcastData) {
            throw new Error('No startBroadcastData');
        }
        const currentStatus = yield* select(getBroadcastStatus);
        const { started_at, stream_uri, owner_uid } = startBroadcastData;

        yield* put(
            updateBroadcastSettings({
                status: currentStatus === BroadcastStatus.STARTING ? BroadcastStatus.STARTED : currentStatus,
                started_at,
                stream_uri,
                started_by: owner_uid,
            })
        );
    } catch (error) {
        logger.error(
            toLoggableError(error, {
                method: 'startBroadcastSaga',
            })
        );

        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.START_BROADCAST_FAILED]()));

        yield* put(updateBroadcastSettings({ status: previousStatus }));
    }
}

function* stopBroadcastSaga() {
    if (!broadcastManager.isReady) {
        return;
    }

    const previousStatus = yield* select(getBroadcastStatus);

    try {
        yield* call([broadcastManager, broadcastManager.stopBroadcast]);

        yield* put(
            updateBroadcastSettings({
                status: BroadcastStatus.FINISHED,
            })
        );
    } catch (error) {
        logger.error(
            toLoggableError(error, {
                method: 'stopBroadcastSaga',
            })
        );

        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.STOP_BROADCAST_FAILED]()));
        yield* put(updateBroadcastSettings({ status: previousStatus }));
    }
}

function* endBroadcastSaga({ payload }: ReturnType<typeof endBroadcastAction>) {
    if (!broadcastManager.isReady) {
        return;
    }

    const { notifyOnError = false } = payload ?? {};
    try {
        yield* call([broadcastManager, broadcastManager.endBroadcast]);
        yield* put(
            updateBroadcastSettings({
                status: BroadcastStatus.CREATED,
            })
        );
    } catch (error) {
        logger.error(
            toLoggableError(error, {
                method: 'endBroadcastSaga',
            })
        );
        if (notifyOnError) {
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.END_BROADCAST_FAILED]()));
        }
    }
}

function* shareBroadcastSaga() {
    const uri = yield* select(getBroadcastUri);

    if (uri) {
        const isMobile = yield* select(isMobileUa);
        const isSuccess = yield* call(copy, uri, isMobile);
        if (isSuccess) {
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.SHARE]()));
        }
    }
}

function* subscribeOnVHLiveEventsSaga() {
    let vhEventsChannel: EventChannel<VHLiveEventData> | undefined;
    try {
        const streamUrl = yield* select(getUgcLiveUrl);
        if (!streamUrl) {
            throw new Error('No stream url');
        }

        if (!vhEventsChannel) {
            vhEventsChannel = yield* call(createVHLiveEventsChannel, { streamUrl, pollUntilOnAir: true });
        }

        while (true) {
            const data = yield* take(vhEventsChannel);
            yield* put(updateVHLiveState(data));
        }
    } catch (error) {
        logger.error(
            toLoggableError(error, {
                method: 'subscribeOnVHLiveEventsSaga',
            })
        );
    } finally {
        const isCancelled = yield* cancelled();
        if (isCancelled && vhEventsChannel) {
            vhEventsChannel.close();
            yield* put(resetVHLiveState());
        }
    }
}

function* watchBroadcastState() {
    try {
        let watchEventsSaga;
        while (true) {
            const prevBroadcastData = yield* select(getBroadcastData);
            const { endCallAction } = yield* race({
                endCallAction: take(cancelCallAction.type),
                broadcastDataChange: race([
                    take(initBroadcastSettings.type),
                    take(updateBroadcastSettings.type),
                    take(setRoomConferenceConfig.type),
                ]),
            });

            if (endCallAction && watchEventsSaga) {
                yield* cancel(watchEventsSaga);
                watchEventsSaga = undefined;
            }

            const broadcastData = yield* select(getBroadcastData);
            const joinedChatId = yield* select(getJoinedChatId);
            const isChatAllowed = yield* select(isChatAllowedInConference);

            const broadCastChatId = broadcastData?.broadcast_chat_path;
            const isTranslatorIntegration = yield* select(selectIsTranslatorIntegration);

            if (
                broadCastChatId &&
                prevBroadcastData?.broadcast_chat_path !== broadCastChatId &&
                broadCastChatId !== joinedChatId &&
                !isTranslatorIntegration &&
                isChatAllowed
            ) {
                yield* put(setJoinedChatId(undefined));
                yield* call(joinTheChatSaga);
            }

            const isMobile = yield* select(isMobileUa);
            const isMeetingRoomIntegration = yield* select(selectIsMeetingRoomIntegration);
            const isRoomAgent = yield* select(isRoomAgentMode);
            if (
                !prevBroadcastData?.broadcast_chat_path &&
                broadcastData?.broadcast_chat_path &&
                !isMobile &&
                !isMeetingRoomIntegration &&
                !isTranslatorIntegration &&
                !isRoomAgent &&
                isChatAllowed
            ) {
                yield* put(setSidebarContent(SidebarContent.Chat));
            }

            // фикс https://nda.ya.ru/t/Z0tmYQCk7BQVVH
            const ugcLiveStatus = yield* select(getVhliveUgcStatus);
            if (
                broadcastData?.status === BroadcastStatus.STARTED &&
                (prevBroadcastData?.status !== BroadcastStatus.STARTED || !ugcLiveStatus)
            ) {
                if (watchEventsSaga) {
                    yield* cancel(watchEventsSaga);
                }
                watchEventsSaga = yield* fork(subscribeOnVHLiveEventsSaga);
            }
            if (
                watchEventsSaga &&
                broadcastData?.status !== BroadcastStatus.STARTED &&
                prevBroadcastData?.status === BroadcastStatus.STARTED
            ) {
                yield* cancel(watchEventsSaga);
                watchEventsSaga = undefined;
            }
        }
    } catch (error) {
        logger.error(
            toLoggableError(error, {
                method: 'watchBroadcastState',
            })
        );
    }
}

export function* broadcastStateChangeSaga(broadcastData: BroadcastData) {
    yield* put(initBroadcastSettings(broadcastData));
}

export function* rootBroadcastSaga() {
    yield* all([
        takeLeading(createBroadcastAction.type, createBroadcastSaga),
        takeLeading(startBroadcastAction.type, startBroadcastSaga),
        takeLeading(stopBroadcastAction.type, stopBroadcastSaga),
        takeLeading(shareBroadcastAction.type, shareBroadcastSaga),
        takeLeading(endBroadcastAction, endBroadcastSaga),
        fork(watchBroadcastState),
    ]);
}
